import { useMemo, useEffect, useState } from 'react';
import { fetchProductsBySearchCriteria, fetchCustomAttributeData } from '../../services/magento/api';
import { getCustomAttribute, extractColors, extractSizes, getLabelFromCustomAttributeValue} from './helper'
import '../styles/ProductPage.css';

// Type filters (you can adjust if needed)
const isApparel = (item) => item.type === 'apparel';
const isHat = (item) => item.type === 'hat';

const StepCustomize = ({ data, updateData, setStepValidator, stepKey, stepIndex, page }) => {
  const [enhancedItems, setEnhancedItems] = useState([]);
  const [allItems, setAllItems] = useState([]);

  const [activeSelection, setActiveSelection] = useState({
    id: null,
    color: '',
    size: '',
  });

  const selectedItems = useMemo(() => data[stepKey] || [], [data, stepKey]);
  const stepType = selectedItems.length > 0 ? selectedItems[0].type : null;

  const [loading, setLoading] = useState(true);

  const [colorOptions, setColorOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);

  useEffect(() => {
    const fetchOptions = async () => {
      const colorData = await fetchCustomAttributeData('138'); // select_color attribute_id
      const sizeData = await fetchCustomAttributeData('154'); // fashion_size attribute_id
      setColorOptions(colorData);
      setSizeOptions(sizeData);
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);

        const filters = [
          { filters: [{ field: 'category_id', value: '965' }] },
          { filters: [{ field: 'status', value: '1' }] },
        ];

        const result = await fetchProductsBySearchCriteria(filters);

        if (!result?.items?.length) {
          console.log('No products fetched');
          setEnhancedItems([]);
          return;
        }

        const allItemsRaw = result.items;
        setAllItems(allItemsRaw);

        const visibleItems = result.items.filter(item => item.visibility !== 1);
        const configurableItems = allItemsRaw.filter(item => item.type_id === 'configurable');

        const childrenByParentId = new Map();

        configurableItems.forEach(config => {
          const childIds = config.extension_attributes?.configurable_product_links?.map(String) || [];
          const children = allItemsRaw.filter(p => childIds.includes(String(p.id)));
          childrenByParentId.set(config.id, children);
        });

        const enrichedConfigurables = configurableItems.map(config => ({
          ...config,
          children: childrenByParentId.get(config.id) || [],
        }));

        const enrichedVisibleItems = [
          ...enrichedConfigurables,
          ...visibleItems.filter(item => item.type_id !== 'configurable'),
        ];

        const withTypes = enrichedVisibleItems.map(item => {
          const skuType = getCustomAttribute(item, 'profitmaker_sku')?.toLowerCase();
          return {
            ...item,
            type: skuType === 'hat' ? 'hat' : 'apparel',
          };
        });

        const tier1Items = withTypes
          .filter(item => getCustomAttribute(item, 'profitmaker_sizeColor') === 'Tier_1')
          .map(item => ({ ...item, tier: 'tier1' }));

        const tier2Items = withTypes
          .filter(item => getCustomAttribute(item, 'profitmaker_sizeColor') === 'Tier_2')
          .map(item => ({ ...item, tier: 'tier2' }));

        setEnhancedItems([...tier1Items, ...tier2Items]);
      } catch (error) {
        console.error('Error fetching products:', error);
        setEnhancedItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  useEffect(() => {
    setStepValidator(() => () => {
      const hasPremium = selectedItems.some(i => i.tier === 'tier2');
      const tier1Count = selectedItems.filter(i => i.tier === 'tier1').length;

      if (hasPremium && selectedItems.length !== 1) return false;
      if (!hasPremium && (tier1Count < 1 || tier1Count > 2)) return false;
      return true;
    });
  }, [selectedItems, setStepValidator]);

  const allSelectedItems = Object.values(data)
    .flat()
    .filter(item => item && typeof item === 'object' && 'id' in item);
  const selectedIds = new Set(allSelectedItems.map(item => item.id));
  const selectedTypesInStep = new Set(selectedItems.map(i => i.type));

  const filteredTier1Items = enhancedItems.filter(
    item => item.tier === 'tier1' && !selectedIds.has(item.id)
  );

  const filteredTier2Items = selectedTypesInStep.size === 0
    ? enhancedItems.filter(item => item.tier === 'tier2' && !selectedIds.has(item.id))
    : enhancedItems.filter(
        item => item.tier === 'tier2' && selectedTypesInStep.has(item.type) && !selectedIds.has(item.id)
      );

  const isPremiumSelected = selectedItems.some(i => i.tier === 'tier2');
  const tier1Count = selectedItems.filter(i => i.tier === 'tier1').length;

  const handleItemSelect = (item, tier) => {
    if (selectedItems.some(i => i.id === item.id)) return;

    const hasPremium = selectedItems.some(i => i.tier === 'tier2');
    const tier1Count = selectedItems.filter(i => i.tier === 'tier1').length;

    if (hasPremium) return;
    if (tier === 'tier2' && tier1Count > 0) return;
    if (tier === 'tier1' && tier1Count >= 2) return;

    const newItems = [
      ...selectedItems,
      { ...item, tier, selectedColor: activeSelection.color, selectedSize: activeSelection.size },
    ];
    updateData({ [stepKey]: newItems });
    setActiveSelection({ id: null, color: '', size: '' });
  };

  const handleRemove = (id) => {
    const newItems = selectedItems.filter(i => i.id !== id);
    updateData({ [stepKey]: newItems });
  };

  const renderItemCard = (item, tier) => {
    const isDisabled =
      selectedItems.some(i => i.id === item.id) ||
      (tier === 'tier2' && (
        selectedItems.some(i => i.tier === 'tier1') ||
        selectedItems.filter(i => i.tier === 'tier2').length >= 1
      )) ||
      (
        tier === 'tier1' &&
        (
          selectedItems.some(i => i.tier === 'tier2') ||
          (tier1Count >= 2 && !selectedItems.some(i => i.id === item.id)) ||
          (
            item.type === 'hat' &&
            stepIndex === 0 &&
            (
              selectedItems.filter(i => i.tier === 'tier1' && i.type === 'apparel').length === 0 ||
              selectedItems.filter(i => i.tier === 'tier1' && i.type === 'hat').length > 0
            )
          )
        )
      );

    return (
        <div
          key={item.id}
          className={`item-card ${isDisabled ? 'disabled' : ''}`}
          onClick={() => {
            if (!isDisabled) {
              setActiveSelection({ id: item.id, color: '', size: '' });
            }
          }}
        >
          {item.media_gallery_entries && item.media_gallery_entries.length > 0 ? (
            (() => {
              const mediaUrl = item.media_gallery_entries[0].file;
              const image = mediaUrl
                ? `https://of3.stran.store/media/catalog/product${mediaUrl}`
                : '/nexeo_logo.png';
              return <img src={image} alt={item.name} />;
            })()
          ) : (
            <img src="/nexeo_logo.png" alt={item.name} />
          )}
          <div className="item-info">
            <h4>{item.name}</h4>
            {/* <p>${typeof item.price === 'number' ? item.price.toFixed(2) : '0.00'}</p> */}
            {tier === 'tier2' && <span className="premium-badge">Premium</span>}
          </div>

          {activeSelection.id === item.id && (
            <div className="dropdowns" onClick={e => e.stopPropagation()}>
              {/* Color Dropdown (only shown if extractColors returns values) */}
              {extractColors(item, allItems, colorOptions).length > 0 && (
                <select
                  value={activeSelection.color}
                  onChange={e =>
                    setActiveSelection(prev => ({ ...prev, color: e.target.value }))
                  }
                >
                  <option value="">Select Color</option>
                  {extractColors(item, allItems, colorOptions).map(color => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))}
                </select>
              )}

              {/* Size Dropdown (only shown if extractSizes returns values) */}
              {extractSizes(item, allItems, sizeOptions).length > 0 && (
                <select
                  value={activeSelection.size}
                  onChange={e =>
                    setActiveSelection(prev => ({ ...prev, size: e.target.value }))
                  }
                >
                  <option value="">Select Size</option>
                  {extractSizes(item, allItems, sizeOptions).map(size => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </select>
              )}

              <button
                onClick={e => {
                  e.stopPropagation();
                  if (
                    (extractColors(item, allItems, colorOptions).length > 0 && !activeSelection.color) ||
                    (extractSizes(item, allItems, sizeOptions).length > 0 && !activeSelection.size)
                  ) {
                    alert('Please select both color and size.');
                    return;
                  }
                  handleItemSelect(item, item.tier);
                }}
              >
                Confirm Add
              </button>
            </div>
          )}

        </div>
    );
  };

  return (
    <div className="customize-step-container">
      {loading ? (
      <div className="loading-message">Loading items...</div>
    ) : (
      <>
        <h2>{page}</h2>
        <p>Due to product availability, please select your additional item selections in order of preference. Additional items consist of an apparel item and a hat, or 1 premium apparel item. We will ask you to make these selections 3 times. If one of your First Preference items is not available, we will defer to your Second Preference for that item and so on.</p>

        {selectedItems.length > 0 && (
          <div className="selected-items-preview">
            <h4>Cart</h4>
            <ul>
              {selectedItems.map(item => (
                <li key={item.id} className="selected-item">
                  <div className="box">
                    <span>Item: {item.name}  {getLabelFromCustomAttributeValue(sizeOptions, item.selectedSize)}  {getLabelFromCustomAttributeValue(colorOptions, item.selectedColor)}</span>
                    <span>Type: {item.type}</span>
                    <span className={`tag ${item.tier}`}>{item.tier === 'tier2' ? 'Premium' : 'Standard'}</span>
                  </div>
                  <button className="remove-btn" onClick={() => handleRemove(item.id)}>🗑️</button>
                </li>
              ))}
            </ul>
          </div>
        )}
        </>
      )}

      <div className="selection-progress">
        <span>Selected: {selectedItems.length} / {isPremiumSelected ? 1 : 2}</span>
      </div>

      <div className="tier-container">
        {!isPremiumSelected && (
          <>
            {(!stepType || stepType === 'hat') && (
              <div className="item-tier tier1">
                <h3>Standard Apparel</h3>
                <div className="items-grid">
                  {filteredTier1Items.filter(isApparel).map(item => renderItemCard(item, 'tier1'))}
                </div>
              </div>
            )}

            {(!stepType || stepType === 'apparel') &&
              selectedItems.some(i => i.tier === 'tier1' && i.type === 'apparel') && (
              <div className="item-tier tier1">
                <h3>Hats</h3>
                <div className="items-grid">
                  {filteredTier1Items.filter(isHat).map(item => renderItemCard(item, 'tier1'))}
                </div>
              </div>
            )}
          </>
        )}

        {tier1Count === 0 && (
          <div className="item-tier tier2">
            <h3>Premium Items</h3>
            <div className="items-grid">
              {filteredTier2Items.map(item => renderItemCard(item, 'tier2'))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepCustomize;
